<!-- Mitt Arbete -->
<script setup lang="ts">
import PieChart from '@/components/PieChart.vue';
import WorkResult from '@/components/results/WorkResult.vue';
import { computed, inject, ref, onMounted, onUnmounted, nextTick } from 'vue';
import { useStore } from '@/stores/main';
import { useIonRouter } from '@ionic/vue';

const props = defineProps<{
  questions: any[];
}>();

const store = useStore();
const router = useIonRouter();
const colorScheme = inject('colorScheme', 'yellow');
const previousSlide = inject('previousSlide');
const nextSlide = inject('nextSlide');

// Add scroll functionality
const scrollButtonVisible = ref(false);
const contentRef = ref(null);
const navigationArea = ref(null);
const isIOS = ref(false);

// Detect iOS device
const detectIOS = () => {
  const userAgent = window.navigator.userAgent.toLowerCase();
  isIOS.value = /iphone|ipad|ipod/.test(userAgent);
};

// Check if scroll is needed
const checkScroll = () => {
  if (contentRef.value) {
    const element = contentRef.value;
    scrollButtonVisible.value = element.scrollHeight > element.clientHeight;
  }
};

// Scroll to bottom function
const scroll = () => {
  if (contentRef.value) {
    // Scroll the container rather than using scrollIntoView
    contentRef.value.scrollTo({
      top: contentRef.value.scrollHeight,
      behavior: 'smooth'
    });
  }
};

// Use onMounted to initialize scroll checking
onMounted(() => {
  detectIOS();
  // Wait for content to render completely
  nextTick(() => {
    checkScroll();
  });
  
  // Add resize and orientation change listeners
  window.addEventListener('resize', checkScroll);
  window.addEventListener('orientationchange', () => {
    // Small delay to ensure the orientation change completes
    setTimeout(checkScroll, 100);
  });
});

// Clean up event listeners
onUnmounted(() => {
  window.removeEventListener('resize', checkScroll);
  window.removeEventListener('orientationchange', checkScroll);
});

const backgroundColor = computed(() => {
  return {
    purple: 'bg-purple-background',
    yellow: 'bg-yellow-background',
    green: 'bg-green-background',
    blue: 'bg-blue-background',
  }[colorScheme];
});
</script>

<template>
  <div
    :class="backgroundColor"
    class="w-full h-full flex flex-col overflow-hidden text-[#171717]"
  >
    <!-- Responsive scrolling container with ref -->
    <div 
      ref="contentRef"
      :class="{'ios-scroll-fix': isIOS}"
      class="flex-1 overflow-y-auto -webkit-overflow-scrolling-touch my-4 px-4 sm:px-6 lg:px-8"
    >
      <div class="w-full shrink-0 text-left pl-0 sm:pl-4 md:pl-[20px]">
        <h2 class="font-light text-[22px] sm:text-[25px] lg:text-[35px] mt-6 sm:mt-8 mb-2">
          Bra jobbat, se resultatet!
        </h2>
        <div class="text-base sm:text-lg lg:text-2xl mt-2 mb-4 text-start">
          Klicka på en tårtbit för att se mer information om ditt resultat.
        </div>
      </div>
      
      <!-- Responsive grid layout for different screen sizes -->
      <div class="w-full px-0 sm:px-4 lg:px-8 lg:pl-[20%] xl:pl-[30%] flex flex-col items-center lg:items-start">
        <WorkResult :factors="store.profile?.factors" :questions="questions" />
      </div>
    </div>

    <!-- Fixed position footer for navigation with better responsive handling -->
    <div
      class="w-full lg:bg-[#FFF3D2] px-4 sm:px-6 lg:px-8 py-4 sm:py-6 button-border relative safe-area-bottom"
      ref="navigationArea"
    >
      <div class="absolute -top-6 right-2" v-if="scrollButtonVisible">
        <button 
          @click="scroll" 
          class="p-2 rounded-full bg-gray-200 touch-action-manipulation"
          aria-label="Scroll to bottom"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>
      
      <!-- Responsive button layouts for all device sizes -->
      <div class="grid grid-cols-2 gap-2 sm:gap-4 max-w-md mx-auto lg:max-w-sm">
        <b-button
          @click="previousSlide"
          look="secondary"
          class="text-sm sm:text-base lg:text-lg w-full min-w-[100px] sm:min-w-[120px] touch-action-manipulation"
        >
          Tillbaka
        </b-button>
        <b-button 
          @click="nextSlide" 
          class="text-sm sm:text-base lg:text-lg w-full min-w-[100px] sm:min-w-[120px] touch-action-manipulation"
        >
          Nästa
        </b-button>
      </div>
    </div>
  </div>
</template>

<style scoped lang="postcss">
.button-border {
  box-shadow: 0px -0.5px 2px 0px rgba(0, 0, 0, 0.25);
}

.lifestyle-result {
  border-bottom: 2px solid #eee;
  padding-bottom: 20px;
}

/* Add iOS-specific fixes */
.safe-area-bottom {
  padding-bottom: env(safe-area-inset-bottom, 8px);
}

/* Ensure proper touch scrolling on iOS */
.-webkit-overflow-scrolling-touch {
  -webkit-overflow-scrolling: touch;
}

/* Additional iOS fixes */
.ios-scroll-fix {
  /* Fix for momentum scrolling issues on iOS */
  -webkit-overflow-scrolling: touch;
  /* Prevent elastic bounce effect on iOS */
  overscroll-behavior: none;
}

/* Improve touch targets for better interaction */
.touch-action-manipulation {
  touch-action: manipulation;
}

/* Media queries for responsive design */
@media screen and (max-width: 320px) {
  /* Extra small devices (iPhone SE, etc) */
  .button-border {
    padding-left: 12px;
    padding-right: 12px;
  }
}

@media screen and (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
  /* Tablets in landscape mode */
  .button-border {
    padding-bottom: 12px;
  }
}

/* Fix for notched devices */
@supports (padding: max(0px)) {
  .safe-area-bottom {
    padding-bottom: max(8px, env(safe-area-inset-bottom, 8px));
  }
}

/* Fix for specific iOS version issues */
@supports (-webkit-touch-callout: none) {
  /* iOS-only style fixes */
  .ios-scroll-fix {
    position: relative;
    z-index: 1;
  }
}
</style>